import React from 'react';
import { Link } from 'react-router-dom';

import { useParentTheme } from '../contexts/ParentThemeContext';

import landingBanner from '../assets/brand/mypiggy-landing-banner.webp';
import './EntryPortal.css';
import AppDownloadCard from '../components/Downloads/AppDownloadCard';
import parentLoginIllustration
  from '../assets/icons/portal/parent-login.png';
import kidLoginIllustration
  from '../assets/icons/portal/kid-login.png';

const EntryPortal = () => {
  const { isDark } = useParentTheme();

  return (
    <div className={`mypiggy-entry${isDark ? ' dark' : ''}`}>
      <main className="relative min-h-screen overflow-hidden bg-gradient-to-br from-teal-50 via-white to-rose-50 px-4 py-8 text-slate-900 transition-colors duration-200 dark:from-slate-950 dark:via-slate-900 dark:to-teal-950 dark:text-slate-100 sm:px-6 sm:py-12">
        <div
          className="pointer-events-none absolute -left-24 top-14 h-64 w-64 rounded-full bg-pink-200/45 blur-3xl dark:bg-pink-500/10"
          aria-hidden="true"
        />

        <div
          className="pointer-events-none absolute -right-24 bottom-8 h-72 w-72 rounded-full bg-teal-200/50 blur-3xl dark:bg-teal-400/10"
          aria-hidden="true"
        />

        <div className="relative mx-auto flex min-h-[calc(100vh-4rem)] w-full max-w-5xl flex-col items-center justify-center">
          <header className="mb-7 w-full text-center sm:mb-8">
            <h1 className="sr-only">MyPiggy Accounting</h1>
            <div className="overflow-hidden rounded-3xl bg-white shadow-sm ring-1 ring-slate-200/70 sm:rounded-[2rem] dark:ring-white/10">
              <div className="aspect-[3.8/1] overflow-hidden sm:aspect-auto">
                <img
                  src={landingBanner}
                  alt=""
                  aria-hidden="true"
                  width="2172"
                  height="724"
                  className="block h-auto w-full"
                  loading="eager"
                  fetchPriority="high"
                  decoding="async"
                />
              </div>
            </div>
            <p className="mx-auto mt-4 max-w-lg text-base font-bold leading-relaxed text-teal-900 sm:sr-only dark:text-teal-100">
              Real piggy bank. Digital tracking. Lifelong money lessons.
            </p>
          </header>

          <section
            aria-label="Choose your MyPiggy experience"
            className="grid w-full max-w-5xl gap-5 sm:grid-cols-2 sm:gap-7"
          >
            <Link
              to="/parent/login"
              aria-label="Continue to Parent Login"
              className="group relative flex min-h-[310px] flex-col items-center justify-between overflow-hidden rounded-[2rem] border border-teal-200/80 bg-white/95 p-6 text-center shadow-lg shadow-teal-900/5 transition duration-200 hover:-translate-y-1 hover:border-teal-400 hover:shadow-xl hover:shadow-teal-900/10 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-teal-500 focus-visible:ring-offset-4 dark:border-teal-800/70 dark:bg-slate-900/90 dark:hover:border-teal-500 dark:focus-visible:ring-offset-slate-950 sm:min-h-[390px] sm:p-8"
            >
              <div
                className="absolute inset-x-0 top-0 h-28 bg-gradient-to-b from-teal-100/80 to-transparent dark:from-teal-900/35"
                aria-hidden="true"
              />

              <img
                src={parentLoginIllustration}
                alt=""
                aria-hidden="true"
                className="relative h-48 w-48 object-contain transition-transform duration-200 group-hover:scale-[1.04] sm:h-56 sm:w-56"
                loading="eager"
                decoding="async"
              />

              <div className="relative mt-3">
                <h2 className="text-2xl font-extrabold text-teal-900 sm:text-3xl dark:text-teal-200">
                  Parent Login
                </h2>

                <p className="mt-2 text-base font-semibold leading-relaxed text-slate-600 sm:text-lg dark:text-slate-300">
                  Manage your family, guide money habits, and celebrate progress.
                </p>

                <span className="mt-4 inline-flex items-center gap-2 rounded-full bg-teal-700 px-5 py-2.5 font-bold text-white transition group-hover:bg-teal-800 dark:bg-teal-600 dark:group-hover:bg-teal-500">
                  Continue as Parent
                  <span aria-hidden="true">→</span>
                </span>
              </div>
            </Link>

            <Link
              to="/kid/login"
              aria-label="Continue to Kid Login"
              className="group relative flex min-h-[310px] flex-col items-center justify-between overflow-hidden rounded-[2rem] border border-pink-200/90 bg-white/95 p-6 text-center shadow-lg shadow-pink-900/5 transition duration-200 hover:-translate-y-1 hover:border-pink-400 hover:shadow-xl hover:shadow-pink-900/10 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-pink-500 focus-visible:ring-offset-4 dark:border-pink-900/70 dark:bg-slate-900/90 dark:hover:border-pink-500 dark:focus-visible:ring-offset-slate-950 sm:min-h-[390px] sm:p-8"
            >
              <div
                className="absolute inset-x-0 top-0 h-28 bg-gradient-to-b from-pink-100/90 to-transparent dark:from-pink-950/40"
                aria-hidden="true"
              />

              <img
                src={kidLoginIllustration}
                alt=""
                aria-hidden="true"
                className="relative h-48 w-48 object-contain transition-transform duration-200 group-hover:scale-[1.04] sm:h-56 sm:w-56"
                loading="eager"
                decoding="async"
              />

              <div className="relative mt-3">
                <h2 className="text-2xl font-extrabold text-pink-700 sm:text-3xl dark:text-pink-300">
                  Kid Login
                </h2>

                <p className="mt-2 text-base font-semibold leading-relaxed text-slate-600 sm:text-lg dark:text-slate-300">
                  Track your money, finish chores, and save for something awesome.
                </p>

                <span className="mt-4 inline-flex items-center gap-2 rounded-full bg-pink-600 px-5 py-2.5 font-bold text-white transition group-hover:bg-pink-700 dark:bg-pink-500 dark:group-hover:bg-pink-400">
                  Continue as Kid
                  <span aria-hidden="true">→</span>
                </span>
              </div>
            </Link>
          </section>

          <section aria-label="Download MyPiggy apps" className="mt-6 grid w-full max-w-5xl gap-5 sm:grid-cols-2 sm:gap-7">
            <AppDownloadCard audience="parents" />
            <AppDownloadCard audience="kids" />
          </section>

          <p className="mt-8 text-center text-lg font-extrabold leading-relaxed text-teal-950 sm:text-xl dark:text-teal-100">
            One family. Two simple ways to learn and grow together.
          </p>
        </div>
      </main>
    </div>
  );
};

export default EntryPortal;

